import { Cancel, Delete, Edit, Save, Warning } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import {
  CreateInvoiceArticle,
  Invoice,
  InvoiceArticle,
  PatchInvoiceArticle,
  Tax,
  Unit
} from '@thro-types/billing';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetArticlesListQuery } from 'api/hooks/billing/articles';
import { useInvoiceArticleMutation } from 'api/hooks/billing/invoiceArticles';
import { useGetSalesTaxesListQuery } from 'api/hooks/billing/tax';
import SubmittingLoader from 'app/components/Util/SubmittingLoader';
import { translations } from 'locales/i18n';
import { formatMonetaryValue } from 'utils/formatMonetaryValue';

import { getTaxOptionsArray } from '../../components/helper';
import {
  hasChangedDefaultTax,
  hasTaxNotZero,
  isForeignCustomer,
  isFreeTextArticle,
  shouldUpdateOrSplitSalestaxes,
  shouldUpdateOrSplitSalestaxesCreateInvoiceArticle
} from '../../utils';
import { BillingCurrencyFieldText } from './BillingCurrencyFieldText';
import { SplitOrChangeTaxDialog } from './SplitOrChangeTaxDialog';

interface Props {
  article: InvoiceArticle;
  invoice: Invoice;
  editingArticle: string | undefined;
  setEditingArticle: (id: string | undefined) => void;
  handleDeleteClick: (article: InvoiceArticle) => void;
  editMode: boolean;
}
export const ExistingInvoiceArticleRow = ({
  article,
  invoice,
  editingArticle,
  setEditingArticle,
  handleDeleteClick,
  editMode
}: Props) => {
  const { t } = useTranslation();
  const [updateArticle, setUpdateArticle] = useState<PatchInvoiceArticle>(article);
  const [splitOrChangeTaxDialogOpen, setSplitOrChangeTaxDialogOpen] = useState(false);
  const [splitArticle, setSplitArticle] = useState<boolean>(false);

  const { update } = useInvoiceArticleMutation({ params: { splitting: splitArticle } });
  const { data: salesTaxes } = useGetSalesTaxesListQuery();

  const isEditingArticle = editingArticle === article.id && editMode;

  const updateInvoiceArticle = async () => {
    await update.mutateAsync({
      invoiceId: invoice.id,
      id: article.id,
      data: {
        amount: updateArticle.amount,
        name: updateArticle.name,
        description: updateArticle.description,
        salestaxVersionId: updateArticle.salestaxVersionId,
        price: Number(updateArticle.price),
        unit: updateArticle.unit
      }
    });
  };

  const handleEditArticle = async () => {
    if (shouldUpdateOrSplitSalestaxes(invoice, updateArticle, article)) {
      //Opens Dialog to split invoice or change all taxes
      setSplitOrChangeTaxDialogOpen(true);
    } else {
      await updateInvoiceArticle();
    }
    setEditingArticle(undefined);
  };

  const handleClose = () => {
    if (update.isPending) {
      return;
    }
    setSplitOrChangeTaxDialogOpen(false);
    handleCancelEditArticle();
  };

  const handleCancelEditArticle = () => {
    setUpdateArticle(article);
    setEditingArticle(undefined);
  };

  const isFreeText = isFreeTextArticle(article);

  return (
    <>
      <Tooltip
        title={
          isFreeText && !isEditingArticle
            ? t(translations.pages.billing.tooltips.isFreeTextArticle())
            : ''
        }
      >
        <TableRow sx={isFreeText ? { backgroundColor: '#f6f6f6' } : undefined}>
          <TableCell>
            {isEditingArticle ? (
              <TextField
                type="text"
                size="small"
                defaultValue={article.name}
                onChange={e => setUpdateArticle(article => ({ ...article, name: e.target.value }))}
                fullWidth
                slotProps={{ input: { style: { fontSize: 14 } } }}
                multiline
              />
            ) : (
              article.name
            )}
          </TableCell>
          <TableCell>
            {isEditingArticle ? (
              <TextField
                type="text"
                defaultValue={article.description}
                size="small"
                onChange={e =>
                  setUpdateArticle(article => ({ ...article, description: e.target.value }))
                }
                fullWidth
                slotProps={{ input: { style: { fontSize: 14 } } }}
                multiline
              />
            ) : (
              article.description
            )}
          </TableCell>
          <TableCell align="right">
            {isEditingArticle && (invoice.state === 'DRAFT' || 'READY' || 'READY') ? (
              <Select
                size="small"
                value={salesTaxes.data ? updateArticle.salestaxVersionId || '' : ''}
                onChange={e =>
                  setUpdateArticle(article => ({ ...article, salestaxVersionId: e.target.value }))
                }
                slotProps={{ root: { style: { fontSize: 14 } } }}
                fullWidth
              >
                {salesTaxes.data.map(tax => (
                  <MenuItem
                    value={tax.id}
                    key={tax.id}
                    style={{
                      fontSize: 14,
                      borderColor: article.article?.defaultTaxId === tax.id ? '#777' : '',
                      borderWidth: article.article?.defaultTaxId === tax.id ? 1 : undefined,
                      borderStyle: article.article?.defaultTaxId === tax.id ? 'dashed' : undefined
                    }}
                  >
                    {tax.text}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <>
                {hasChangedDefaultTax(article) && (
                  <Tooltip title={t(translations.pages.billing.tooltips.defaultTaxChanged())}>
                    <Warning color="error" style={{ marginRight: 10 }} />
                  </Tooltip>
                )}
                {isForeignCustomer(invoice.customer!) && hasTaxNotZero(article) && (
                  <Tooltip title={t(translations.pages.billing.tooltips.foreignTaxNotNull())}>
                    <Warning color="error" style={{ marginRight: 10 }} />
                  </Tooltip>
                )}
                {`${article.salestax?.taxRate}%`}
              </>
            )}
          </TableCell>
          <TableCell align="right">
            {isEditingArticle ? (
              <TextField
                type="number"
                defaultValue={article.amount}
                size="small"
                onChange={e =>
                  setUpdateArticle(article => ({
                    ...article,
                    amount: parseInt(e.target.value, 10)
                  }))
                }
                slotProps={{ input: { style: { fontSize: 14 } } }}
              />
            ) : (
              article.amount
            )}
          </TableCell>
          <TableCell align="left">
            {isEditingArticle ? (
              <Select
                type="number"
                defaultValue={article.unit}
                size="small"
                onChange={e =>
                  setUpdateArticle(article => ({ ...article, unit: e.target.value as Unit }))
                }
                slotProps={{ root: { style: { fontSize: 14 } } }}
              >
                {Object.values(Unit).map(unit => (
                  <MenuItem value={unit} key={unit} style={{ fontSize: 14 }}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              article.unit
            )}
          </TableCell>
          <TableCell align="right">
            {isEditingArticle ? (
              <BillingCurrencyFieldText
                value={article.price || 0}
                onChange={e => setUpdateArticle(article => ({ ...article, price: e }))}
              />
            ) : (
              formatMonetaryValue(`${article.price}`)
            )}
          </TableCell>
          <TableCell align="right">
            {formatMonetaryValue(`${article.price * article.amount}`)}
          </TableCell>
          {editMode && (
            <TableCell align="center">
              {isEditingArticle ? (
                <Grid2 container justifyContent={'center'} gap={1}>
                  <IconButton color="error" size="small" onClick={handleCancelEditArticle}>
                    <Cancel />
                  </IconButton>
                  <IconButton color="primary" size="small" onClick={handleEditArticle}>
                    <Save />
                  </IconButton>
                </Grid2>
              ) : (
                <>
                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={() => setEditingArticle(article.id)}
                    disabled={update.isPending}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={() => handleDeleteClick(article)}
                    disabled={update.isPending}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              )}
            </TableCell>
          )}
        </TableRow>
      </Tooltip>
      <SplitOrChangeTaxDialog
        open={splitOrChangeTaxDialogOpen}
        handleClose={handleClose}
        setSplitArticle={setSplitArticle}
        setSplitOrChangeTaxDialogOpen={setSplitOrChangeTaxDialogOpen}
        updateOrCreateArticle={updateInvoiceArticle}
        isSubmitting={update.isPending}
        salesTaxVersionId={updateArticle.salestaxVersionId}
      />
    </>
  );
};

export const NewInvoiceArticleRow = ({ invoice }: { invoice: Invoice }) => {
  const { t } = useTranslation();

  const { data: availableArticles } = useGetArticlesListQuery();

  const [splitOrChangeTaxDialogOpen, setSplitOrChangeTaxDialogOpen] = useState(false);
  const [splitArticle, setSplitArticle] = useState<boolean>(false);

  const filteredArticles = availableArticles?.data.filter(
    article => !invoice.invoiceArticles.map(article => article.sku).includes(article.sku)
  );

  const { data: salesTaxes } = useGetSalesTaxesListQuery();
  const [article, setArticle] = useState<CreateInvoiceArticle & { defaultTax?: Tax | null }>({
    name: '',
    description: '',
    amount: 1,
    price: 0,
    sku: '',
    unit: Unit.LE,
    salestaxVersionId: ''
  });

  const { create } = useInvoiceArticleMutation({ params: { splitting: splitArticle } });
  const isValid =
    article.name &&
    typeof article.price === 'number' &&
    article.amount &&
    article.unit &&
    article.salestaxVersionId;

  const createInvoiceArticle = async () => {
    await create.mutateAsync({ invoiceId: invoice.id, data: article });
    setArticle({ name: '', description: '', amount: 1, price: 0, sku: '', unit: 'LE' });
  };

  const handleCreate = async () => {
    if (shouldUpdateOrSplitSalestaxesCreateInvoiceArticle(invoice, article)) {
      setSplitOrChangeTaxDialogOpen(true);
    } else {
      await createInvoiceArticle();
    }
  };

  const handleClose = () => {
    if (create.isPending) {
      return;
    }
    setSplitOrChangeTaxDialogOpen(false);
  };

  const getTaxOptions = () => {
    return getTaxOptionsArray(salesTaxes.data, article).map(tax => (
      <MenuItem value={tax.id} key={tax.id} style={{ fontSize: 14 }}>
        {tax.text}
      </MenuItem>
    ));
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Autocomplete
            freeSolo
            options={filteredArticles || []}
            getOptionLabel={article => {
              if (typeof article === 'string') {
                return article;
              }
              return article.sku ? article.name : '';
            }}
            renderOption={(props, article) => (
              <li {...props}>
                <span>
                  {article.name} {article.description}
                </span>
              </li>
            )}
            isOptionEqualToValue={(option, value) => {
              return option.sku === value.sku;
            }}
            renderInput={params => (
              <TextField
                {...params}
                slotProps={{ htmlInput: { ...params.inputProps, style: { fontSize: 14 } } }}
                placeholder={t(translations.pages.billing.newArticle())}
              />
            )}
            size="small"
            inputValue={article.name}
            onChange={(_, value) => {
              if (value && typeof value === 'object') {
                const tax = isForeignCustomer(invoice.customer!)
                  ? salesTaxes.data.find(tax => tax.key === '00')?.id
                  : value.defaultTaxId;
                setArticle({
                  ...article,
                  ...value,
                  price: parseInt(value?.price.toString() || '0'),
                  salestaxVersionId: tax
                });
              }
              if (!value) {
                setArticle(article => ({ ...article, name: '' }));
              }
            }}
            onInputChange={(_, value) => {
              setArticle(article => ({ ...article, name: value }));
            }}
            ListboxProps={{ style: { fontSize: 14 } }}
            slotProps={{ paper: { style: { fontSize: 14 } } }}
          />
        </TableCell>
        <TableCell>
          <TextField
            type="text"
            size="small"
            fullWidth
            value={article.description}
            onChange={e => setArticle(article => ({ ...article, description: e.target.value }))}
            slotProps={{ input: { style: { fontSize: 14 } } }}
            multiline
          />
        </TableCell>
        <TableCell align="right">
          <Select
            size="small"
            value={salesTaxes.data ? article.salestaxVersionId || '' : ''}
            onChange={e =>
              setArticle(article => ({ ...article, salestaxVersionId: e.target.value }))
            }
            slotProps={{ root: { style: { fontSize: 14 } } }}
            fullWidth
          >
            {getTaxOptions()}
          </Select>
        </TableCell>
        <TableCell align="right">
          <TextField
            type="number"
            size="small"
            value={article.amount || ''}
            onChange={e =>
              parseInt(e.target.value) > 0 &&
              setArticle(article => ({ ...article, amount: parseInt(e.target.value) }))
            }
            slotProps={{ input: { style: { fontSize: 14 } } }}
          />
        </TableCell>
        <TableCell align="left">
          <Select
            size="small"
            value={article.unit || ''}
            onChange={e => setArticle(article => ({ ...article, unit: e.target.value as Unit }))}
            slotProps={{ root: { style: { fontSize: 14 } } }}
          >
            {Object.values(Unit).map(unit => (
              <MenuItem value={unit} key={unit}>
                {unit}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell align="right">
          <BillingCurrencyFieldText
            value={article.price || 0}
            onChange={e => setArticle(article => ({ ...article, price: e }))}
          />
        </TableCell>
        <TableCell align="right">
          <BillingCurrencyFieldText value={(article.price || 0) * article.amount || 0} disabled />
        </TableCell>
        <TableCell align="center" style={{ position: 'relative' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleCreate}
            disabled={!isValid || create.isPending}
          >
            {t(translations.common.save())}
          </Button>
          {create.isPending && <SubmittingLoader />}
        </TableCell>
      </TableRow>
      <SplitOrChangeTaxDialog
        open={splitOrChangeTaxDialogOpen}
        handleClose={handleClose}
        setSplitArticle={setSplitArticle}
        setSplitOrChangeTaxDialogOpen={setSplitOrChangeTaxDialogOpen}
        updateOrCreateArticle={createInvoiceArticle}
        isSubmitting={create.isPending}
        salesTaxVersionId={article.salestaxVersionId}
      />
    </>
  );
};
